// Spec 046 Task B: HistoryTab — fetch admin action log from /api/garages/admin/offerings/{id}/history.
import { useState, useEffect } from 'react';
import { api } from '../../services/api';
import { AdminActionView } from '../../types/offeringsV2';

interface Props {
  offeringId: string;
  token: string;
}

type DisplayAction = 'APPROVED' | 'REJECTED' | 'REQUESTED_CHANGES';

function toDisplayAction(action: AdminActionView['action']): DisplayAction {
  if (action === 'OFFERING_APPROVED' || action === 'VARIANT_APPROVED') return 'APPROVED';
  if (action === 'OFFERING_REJECTED' || action === 'VARIANT_REJECTED') return 'REJECTED';
  return 'REQUESTED_CHANGES';
}

function actionLabel(display: DisplayAction): string {
  switch (display) {
    case 'APPROVED': return 'Approved';
    case 'REJECTED': return 'Rejected';
    case 'REQUESTED_CHANGES': return 'Changes requested';
  }
}

function actionBadgeModifier(display: DisplayAction): string {
  switch (display) {
    case 'APPROVED': return 'history-row__badge--approved';
    case 'REJECTED': return 'history-row__badge--rejected';
    case 'REQUESTED_CHANGES': return 'history-row__badge--changes';
  }
}

function shortSubjectId(subjectId: string): string {
  if (subjectId.length > 8) return subjectId.slice(-8);
  return subjectId;
}

// Prefer the resolved name. The short subject id remains the fallback for actors the
// identity service cannot resolve — a deleted account still belongs in the audit trail.
function actorName(entry: AdminActionView): string {
  return entry.adminName?.trim() || `Admin ${shortSubjectId(entry.adminSubjectId)}`;
}

function targetDescription(entry: AdminActionView): string {
  if (entry.variantLabel) return `variant ${entry.variantLabel}`;
  if (entry.offeringId) return `offering ${entry.offeringName ?? 'unknown'}`;
  return 'offering';
}

export default function HistoryTab({ offeringId, token }: Props) {
  const [entries, setEntries] = useState<AdminActionView[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    setError(null);
    api.getOfferingHistory(offeringId, token)
      .then((data) => {
        // Sort descending by id (server already does this — defensive)
        const sorted = [...data].sort((a, b) => b.id - a.id);
        setEntries(sorted);
      })
      .catch(() => setError('Failed to load history'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offeringId]);

  if (loading) {
    return <div className="history-loading">Loading…</div>;
  }

  if (error) {
    return (
      <div className="history-error">
        <span>{error}</span>
        <button onClick={load} className="history-retry-btn">Retry</button>
      </div>
    );
  }

  if (!entries || entries.length === 0) {
    return (
      <div className="history-empty">
        No admin actions yet on this offering.
      </div>
    );
  }

  return (
    <div className="history-list">
      {entries.map((entry) => {
        const display = toDisplayAction(entry.action);
        return (
          <div key={entry.id} className="history-row">
            <span className="history-row__time">
              {new Date(entry.occurredAt).toLocaleString()}
            </span>
            <span className={`history-row__badge ${actionBadgeModifier(display)}`}>
              {actionLabel(display)}
            </span>
            <span className="history-row__detail">
              {actorName(entry)} &rarr; {actionLabel(display)} {targetDescription(entry)}
            </span>
            {entry.reason && (
              <div className="history-row__reason">{entry.reason}</div>
            )}
          </div>
        );
      })}
    </div>
  );
}
